// RFC 8785 (JCS), the canonical JSON the resolved artifact's digest and the text profile's attribute lines use
// (§14.1, §14.2): object keys in UTF-16 code-unit order, no whitespace, ECMAScript number and string serialization.
// JSON.stringify already serializes numbers and strings the way JCS requires; what it leaves is the key order.
import { sha256Hex } from "./digest";

/** `value` as RFC 8785 canonical JSON. Throws on what JSON cannot carry (a non-finite number, a function). */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError("canonical JSON has no non-finite numbers");
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v === undefined ? null : v)).join(",")}]`;
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    // (the default sort compares UTF-16 code units, as JCS orders keys)
    const keys = Object.keys(o).filter((k) => o[k] !== undefined).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(",")}}`;
  }
  throw new TypeError(`canonical JSON cannot carry a ${typeof value}`);
}

/** `sha256:<hex>` of a value's canonical JSON, as UTF-8. */
export function jsonDigest(value: unknown): string {
  return `sha256:${sha256Hex(canonicalJson(value))}`;
}
